import * as THREE from 'three';
import { CSMFrustum } from 'three/addons/csm/CSMFrustum.js';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';

const cameraToLightMatrix = new THREE.Matrix4();
const lightSpaceFrustum = new CSMFrustum();
const center = new THREE.Vector3();
const targetPosition = new THREE.Vector3();
const bbox = new THREE.Box3();
const lightDirection = new THREE.Vector3();
const lightOrientationMatrix = new THREE.Matrix4();
const lightOrientationMatrixInverse = new THREE.Matrix4();
const lightWorldPosition = new THREE.Vector3();
const lightTargetWorldPosition = new THREE.Vector3();
const up = new THREE.Vector3(0, 1, 0);

type CascadeLight = THREE.Object3D & {
  shadow?: THREE.DirectionalLightShadow;
  target: THREE.Object3D;
};

export class WorldSpaceCSMShadowNode extends CSMShadowNode {
  public override updateBefore(_frame: Parameters<CSMShadowNode['updateBefore']>[0]): ReturnType<CSMShadowNode['updateBefore']> {
    const light = this.light as THREE.DirectionalLight;
    const parent = light.parent;
    const camera = this.camera;
    const frustums = this.frustums;

    if (!parent || !camera) {
      return;
    }

    for (const cascadeLight of this.lights as CascadeLight[]) {
      if (cascadeLight.parent === null) {
        parent.add(cascadeLight.target);
        parent.add(cascadeLight);
      }
    }

    light.updateMatrixWorld(true);
    light.target.updateMatrixWorld(true);
    parent.updateMatrixWorld(true);

    light.getWorldPosition(lightWorldPosition);
    light.target.getWorldPosition(lightTargetWorldPosition);
    lightDirection.subVectors(lightTargetWorldPosition, lightWorldPosition).normalize();

    lightOrientationMatrix.lookAt(lightWorldPosition, lightTargetWorldPosition, up);
    lightOrientationMatrixInverse.copy(lightOrientationMatrix).invert();

    for (let idx = 0; idx < frustums.length; idx++) {
      const cascadeLight = this.lights[idx] as CascadeLight;
      const shadow = cascadeLight.shadow;
      if (!shadow) {
        continue;
      }

      const shadowCam = shadow.camera;
      const texelWidth = (shadowCam.right - shadowCam.left) / shadow.mapSize.width;
      const texelHeight = (shadowCam.top - shadowCam.bottom) / shadow.mapSize.height;

      cameraToLightMatrix.multiplyMatrices(lightOrientationMatrixInverse, camera.matrixWorld);
      frustums[idx].toSpace(cameraToLightMatrix, lightSpaceFrustum);

      const nearVerts = lightSpaceFrustum.vertices.near;
      const farVerts = lightSpaceFrustum.vertices.far;

      bbox.makeEmpty();
      for (let vertexIdx = 0; vertexIdx < 4; vertexIdx++) {
        bbox.expandByPoint(nearVerts[vertexIdx]);
        bbox.expandByPoint(farVerts[vertexIdx]);
      }

      bbox.getCenter(center);
      center.z = bbox.max.z + this.lightMargin;
      center.x = Math.floor(center.x / texelWidth) * texelWidth;
      center.y = Math.floor(center.y / texelHeight) * texelHeight;
      center.applyMatrix4(lightOrientationMatrix);

      targetPosition.copy(center).add(lightDirection);
      cascadeLight.position.copy(parent.worldToLocal(center));
      cascadeLight.target.position.copy(parent.worldToLocal(targetPosition));
    }

    return undefined;
  }
}
